/**
 * Package 1B — step-up authentication hook.
 *
 * Manages the elevated session token entirely in sessionStorage — it is
 * cleared when the browser tab closes, which satisfies the requirement
 * that "the unlocked state must never survive a browser restart".
 *
 * Never stores the password. Never uses localStorage. Never returns the
 * token via a URL parameter.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const STEP_UP_KEY = "engage_step_up_token";  // sessionStorage only
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export function useStepUp(authToken) {
  const [state, setState] = useState({
    unlocked: false,
    token: null,
    expiresAt: null,
    remainingSeconds: 0,
    error: null,
    loading: false,
  });

  const _stepUpHeaders = useCallback(() => {
    const t = state.token || sessionStorage.getItem(STEP_UP_KEY) || "";
    const h = {};
    if (authToken) h.Authorization = `Bearer ${authToken}`;
    if (t) h["X-Step-Up-Token"] = t;
    return h;
  }, [state.token, authToken]);

  const refreshStatus = useCallback(async () => {
    if (!authToken) return;
    try {
      const r = await axios.get(`${API}/charity/step-up/status`, { headers: _stepUpHeaders() });
      setState((s) => ({
        ...s,
        unlocked: !!r.data?.unlocked,
        expiresAt: r.data?.expiresAt || null,
        remainingSeconds: r.data?.remainingSeconds || 0,
        error: null,
      }));
      if (!r.data?.unlocked) {
        sessionStorage.removeItem(STEP_UP_KEY);
      }
    } catch (_e) {
      // Fail closed — treat as locked
      setState((s) => ({ ...s, unlocked: false, token: null }));
      sessionStorage.removeItem(STEP_UP_KEY);
    }
  }, [_stepUpHeaders, authToken]);

  // Restore token from sessionStorage on first mount + refresh status.
  useEffect(() => {
    const t = sessionStorage.getItem(STEP_UP_KEY);
    if (t) setState((s) => ({ ...s, token: t }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authToken) refreshStatus();
  }, [authToken, refreshStatus]);

  // Poll every 30s to catch inactivity expiry.
  useEffect(() => {
    if (!state.unlocked) return;
    const iv = setInterval(refreshStatus, 30000);
    return () => clearInterval(iv);
  }, [state.unlocked, refreshStatus]);

  const unlock = useCallback(async (password) => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const r = await axios.post(`${API}/charity/step-up/unlock`,
        { password },
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      const token = r.data?.token;
      if (token) {
        sessionStorage.setItem(STEP_UP_KEY, token);
        setState({
          unlocked: true,
          token,
          expiresAt: r.data.expiresAt,
          remainingSeconds: 900,
          error: null,
          loading: false,
        });
      }
      return { ok: true };
    } catch (e) {
      const code = e?.response?.data?.code || "STEP_UP_ERROR";
      const message = e?.response?.data?.message || "Unlock failed.";
      setState((s) => ({ ...s, loading: false, error: { code, message } }));
      return { ok: false, code, message };
    }
  }, [authToken]);

  const lock = useCallback(async () => {
    try {
      await axios.post(`${API}/charity/step-up/lock`, {}, { headers: _stepUpHeaders() });
    } catch (_e) { /* best-effort */ }
    sessionStorage.removeItem(STEP_UP_KEY);
    setState({ unlocked: false, token: null, expiresAt: null, remainingSeconds: 0, error: null, loading: false });
  }, [_stepUpHeaders]);

  return {
    ...state,
    unlock,
    lock,
    refreshStatus,
    stepUpHeaders: _stepUpHeaders,
  };
}
